import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Search, Loader2, X, Send, Lock, MessageSquare, History } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';
import MultiClassSelect, { CurriculumGroup, ClassFilterChips } from '@/components/common/MultiClassSelect';

const BASE = '/api/parent-app/admin';

type ComplaintStatus   = 'open' | 'in_progress' | 'resolved' | 'closed';
type ComplaintCategory = 'teaching' | 'homework' | 'exam' | 'facility' | 'bus' | 'food' | 'bullying' | 'admin' | 'other';
type ConcernType       = 'academic' | 'non_academic';
type Priority          = 'low' | 'normal' | 'high' | 'urgent';
type HandlingTeam      = 'academic' | 'operations' | 'accounts' | 'admin';
type CommentChannel    = 'internal' | 'parent';

interface Student { id: number; display_name: string; admission_number: string | null; class?: { class_label: string }; section?: { section_label: string }; }
interface StaffRef { id: number; name: string; }
interface Comment {
  id: number;
  channel: CommentChannel;
  body: string;
  author_name: string | null;
  author_role: string | null;
  created_at: string;
}
interface HistoryChange { field: string; old_value: unknown; new_value: unknown; }
interface HistoryEntry {
  id: number;
  action: string;
  changes: HistoryChange[] | null;
  changed_by_name: string | null;
  changed_by_role: string | null;
  changed_at: string;
}
interface Complaint {
  id: number;
  uuid: string;
  category: ComplaintCategory;
  concern_type: ConcernType;
  priority: Priority;
  handling_team: HandlingTeam | null;
  assignee_id: number | null;
  assignee: StaffRef | null;
  title: string;
  description: string;
  status: ComplaintStatus;
  admin_response: string | null;
  responded_at: string | null;
  resolved_at: string | null;
  created_at: string;
  student: Student | null;
  comments?: Comment[];
}

const STATUS_CONFIG: Record<ComplaintStatus, { label: string; color: string }> = {
  open:        { label: 'Open',        color: '#ef4444' },
  in_progress: { label: 'In Progress', color: '#f59e0b' },
  resolved:    { label: 'Resolved',    color: '#22c55e' },
  closed:      { label: 'Closed',      color: '#94a3b8' },
};

const CATEGORY_LABELS: Record<ComplaintCategory, string> = {
  teaching: 'Teaching',
  homework: 'Homework',
  exam:     'Exams',
  facility: 'Facility',
  bus:      'Bus / Transport',
  food:     'Food',
  bullying: 'Bullying',
  admin:    'Admin / Staff',
  other:    'Other',
};

const PRIORITY_CONFIG: Record<Priority, { label: string; color: string }> = {
  low:    { label: 'Low',    color: '#94a3b8' },
  normal: { label: 'Normal', color: '#3b82f6' },
  high:   { label: 'High',   color: '#f59e0b' },
  urgent: { label: 'Urgent', color: '#ef4444' },
};

// Which categories belong to which top-level type (used by the create form).
const ACADEMIC_CATEGORIES: ComplaintCategory[] = ['teaching', 'homework', 'exam'];

const CATEGORIES = Object.entries(CATEGORY_LABELS) as [ComplaintCategory, string][];
const STATUSES   = Object.entries(STATUS_CONFIG)   as [ComplaintStatus, { label: string; color: string }][];
const PRIORITIES = Object.entries(PRIORITY_CONFIG) as [Priority, { label: string; color: string }][];
const TYPE_TABS: { key: '' | ConcernType; label: string }[] = [
  { key: '',             label: 'All' },
  { key: 'academic',     label: 'Academic' },
  { key: 'non_academic', label: 'Non-academic' },
];
const LIMIT = 20;

const HISTORY_FIELD_LABELS: Record<string, string> = {
  status: 'Status', priority: 'Priority', category: 'Category', concern_type: 'Type',
  handling_team: 'Handling team', assignee_id: 'Assignee', section_id: 'Section',
  satisfaction_rating: 'Satisfaction rating', resolved_at: 'Resolved', resolved_by_id: 'Resolved by',
  internal_note: 'Internal note', parent_reply: 'Parent reply', title: 'Title', reason: 'Reason',
};
// Redundant with a more readable sibling change in the same entry (e.g.
// resolved_at/resolved_by_id land alongside the status->resolved change) —
// hide these lines rather than repeat the same fact three ways.
const HISTORY_HIDDEN_FIELDS = new Set(['resolved_at', 'resolved_by_id']);

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtTime(d: string) {
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
}

// ── Detail Modal ──────────────────────────────────────────────────────────────
function DetailModal({ complaint, staff, onClose, onUpdated }: { complaint: Complaint; staff: StaffRef[]; onClose: () => void; onUpdated: () => void }) {
  const [status,    setStatus]    = useState<ComplaintStatus>(complaint.status);
  const [priority,  setPriority]  = useState<Priority>(complaint.priority);
  const [assignee,  setAssignee]  = useState<number | ''>(complaint.assignee_id ?? '');
  const [saving,    setSaving]    = useState(false);

  const [comments,     setComments]     = useState<Comment[]>(complaint.comments ?? []);
  const [commentTab,   setCommentTab]   = useState<CommentChannel>('internal');
  const [draft,        setDraft]        = useState('');
  const [posting,      setPosting]      = useState(false);
  const [loadingC,     setLoadingC]     = useState(true);

  const [history,        setHistory]        = useState<HistoryEntry[]>([]);
  const [loadingHistory,  setLoadingHistory] = useState(true);
  const [historyOpen,    setHistoryOpen]    = useState(false);

  useEffect(() => {
    apiRequest<{ data: Comment[] }>(`${BASE}/complaints/${complaint.id}/comments`)
      .then(res => { if (res.success) setComments(res.data?.data ?? []); })
      .catch(() => {})
      .finally(() => setLoadingC(false));
  }, [complaint.id]);

  useEffect(() => {
    apiRequest<{ data: HistoryEntry[] }>(`${BASE}/change-logs?entity_type=complaint&entity_id=${complaint.id}&limit=50`)
      .then(res => { if (res.success) setHistory(res.data?.data ?? []); })
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [complaint.id]);

  // assignee_id/resolved_by_id land in history as raw ids — resolve to a
  // readable name from the same staff list the Assignee dropdown uses.
  const staffName = (id: unknown) => {
    if (id == null) return 'Unassigned';
    const found = staff.find(s => s.id === Number(id));
    return found?.name ?? `#${id}`;
  };

  function describeChange(c: HistoryChange): string {
    const label = HISTORY_FIELD_LABELS[c.field] ?? c.field;
    if (c.field === 'assignee_id') return `${label}: ${staffName(c.old_value)} → ${staffName(c.new_value)}`;
    if (c.field === 'internal_note' || c.field === 'parent_reply' || c.field === 'reason') return `${label}: "${c.new_value}"`;
    const from = c.old_value == null ? '—' : String(c.old_value);
    const to = c.new_value == null ? '—' : String(c.new_value);
    return `${label}: ${from} → ${to}`;
  }

  const handleSaveMeta = async () => {
    setSaving(true);
    try {
      // status + priority via update; assignment via the assign endpoint.
      const upd = await apiRequest(`${BASE}/complaints/${complaint.id}`, {
        method: 'PUT', body: JSON.stringify({ status, priority }),
      });
      if (!upd.success) return toastMessage(upd.message || 'Failed', 'error');
      const asg = await apiRequest(`${BASE}/complaints/${complaint.id}/assign`, {
        method: 'PUT', body: JSON.stringify({ assignee_id: assignee === '' ? null : Number(assignee) }),
      });
      if (!asg.success) return toastMessage(asg.message || 'Failed', 'error');
      toastMessage('Concern updated');
      onUpdated();
      onClose();
    } catch { toastMessage('Failed', 'error'); }
    finally { setSaving(false); }
  };

  const postComment = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      const res = await apiRequest<{ data: Comment }>(`${BASE}/complaints/${complaint.id}/comments`, {
        method: 'POST', body: JSON.stringify({ channel: commentTab, body: draft.trim() }),
      });
      if (!res.success) return toastMessage(res.message || 'Failed', 'error');
      if (res.data?.data) setComments(c => [...c, res.data!.data]);
      setDraft('');
      toastMessage(commentTab === 'parent' ? 'Reply sent to parent' : 'Note added');
    } catch { toastMessage('Failed', 'error'); }
    finally { setPosting(false); }
  };

  const shown = comments.filter(c => c.channel === commentTab);
  const pri = PRIORITY_CONFIG[complaint.priority];
  const st  = STATUS_CONFIG[complaint.status];
  const isAcademic = complaint.concern_type === 'academic';

  const chip = (text: string, fg: string, bg: string) => (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: bg, color: fg, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>{text}</span>
  );

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="modal" style={{ width: 780, maxWidth: '95vw' }}>

        {/* Header bar */}
        <div style={{ background: 'var(--slate)', color: '#fff', padding: '18px 24px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,0.12)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <AlertTriangle size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.3px', lineHeight: 1.3 }}>{complaint.title}</div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>
                Raised {fmt(complaint.created_at)}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><X size={17} /></button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14 }}>
            {chip(st.label, '#fff', st.color)}
            {chip(isAcademic ? 'Academic' : 'Non-academic', '#fff', 'rgba(255,255,255,0.16)')}
            {chip(CATEGORY_LABELS[complaint.category], '#fff', 'rgba(255,255,255,0.16)')}
            {chip(`${pri.label} priority`, '#fff', pri.color)}
          </div>
        </div>

        <div className="modal-body col" style={{ paddingTop: 18, paddingBottom: 24, gap: 26 }}>

          {/* Child details */}
          <div className="field">
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)' }}>Child details</label>
            {complaint.student ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--surface-tint)', border: '1px solid var(--border)', borderRadius: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--slate-soft)', color: 'var(--slate)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0 }}>
                  {complaint.student.display_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{complaint.student.display_name}</div>
                  <div className="meta" style={{ marginTop: 2 }}>
                    {complaint.student.class ? `${complaint.student.class.class_label}${complaint.student.section ? ` · Section ${complaint.student.section.section_label}` : ''}` : 'Class not set'}
                    {complaint.student.admission_number ? ` · Adm# ${complaint.student.admission_number}` : ''}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding: '12px 14px', background: 'var(--surface-tint)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 13, color: 'var(--text-3)' }}>
                No student linked to this concern.
              </div>
            )}
          </div>

          {/* Description */}
          <div className="field">
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)' }}>Details</label>
            <p style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--text)', margin: 0 }}>{complaint.description}</p>
          </div>

          {/* Manage row: status · priority · assignee */}
          <div style={{ background: 'var(--surface-tint)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="field">
              <label>Status</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {STATUSES.map(([val, c]) => (
                  <button key={val} type="button" onClick={() => setStatus(val)}
                    style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${status === val ? c.color : 'var(--border-2)'}`, background: status === val ? c.color + '18' : 'var(--surface)', color: status === val ? c.color : 'var(--text-2)' }}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="row gap-14" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label>Priority</label>
                <select className="select" value={priority} onChange={e => setPriority(e.target.value as Priority)}>
                  {PRIORITIES.map(([val, c]) => <option key={val} value={val}>{c.label}</option>)}
                </select>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label>Assigned to</label>
                <select className="select" value={assignee} onChange={e => setAssignee(e.target.value === '' ? '' : Number(e.target.value))}>
                  <option value="">Unassigned</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Conversation */}
          <div className="field">
            <label>Conversation</label>
            <div className="tabs" style={{ marginBottom: 12 }}>
              <button className={commentTab === 'internal' ? 'active' : ''} onClick={() => setCommentTab('internal')}>
                <span className="row gap-6" style={{ alignItems: 'center' }}><Lock size={13} /> Internal notes</span>
              </button>
              <button className={commentTab === 'parent' ? 'active' : ''} onClick={() => setCommentTab('parent')}>
                <span className="row gap-6" style={{ alignItems: 'center' }}><MessageSquare size={13} /> Parent replies</span>
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto', marginBottom: 12 }}>
              {loadingC ? (
                <div className="center" style={{ padding: 20 }}><Loader2 className="animate-spin" size={16} style={{ color: 'var(--text-3)' }} /></div>
              ) : shown.length === 0 ? (
                <p className="meta" style={{ margin: 0, padding: '8px 0' }}>
                  {commentTab === 'internal' ? 'No internal notes yet. Notes are visible to staff only.' : 'No replies to the parent yet.'}
                </p>
              ) : shown.map(c => (
                <div key={c.id} style={{ padding: '10px 13px', borderRadius: 10, background: c.channel === 'internal' ? 'var(--surface-tint)' : '#eff6ff', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{c.body}</div>
                  <div className="meta" style={{ marginTop: 5 }}>{c.author_name || 'Staff'}{c.author_role ? ` · ${c.author_role}` : ''} · {fmtTime(c.created_at)}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea rows={2} value={draft} onChange={e => setDraft(e.target.value)}
                placeholder={commentTab === 'internal' ? 'Add an internal note (staff only)…' : 'Write a reply the parent will see…'}
                style={{ flex: 1, resize: 'vertical', padding: '9px 12px', borderRadius: 10, border: '1px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, lineHeight: 1.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              <button className="btn btn-primary" onClick={postComment} disabled={posting || !draft.trim()} style={{ flexShrink: 0 }}>
                {posting ? <Loader2 className="animate-spin" size={14} /> : <><Send size={14} /> Send</>}
              </button>
            </div>
          </div>

          {/* History — who raised it, every status/priority/assignee change,
              and every comment, pulled from pa_change_logs. Collapsed by
              default since it's supplementary to the Conversation above. */}
          <div className="field">
            <button type="button" onClick={() => setHistoryOpen(o => !o)}
              className="row gap-6" style={{ alignItems: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-2)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <History size={13} /> History {history.length > 0 ? `(${history.length})` : ''}
              <span style={{ marginLeft: 4 }}>{historyOpen ? '▲' : '▼'}</span>
            </button>
            {historyOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto', marginTop: 10 }}>
                {loadingHistory ? (
                  <div className="center" style={{ padding: 12 }}><Loader2 className="animate-spin" size={16} style={{ color: 'var(--text-3)' }} /></div>
                ) : history.length === 0 ? (
                  <p className="meta" style={{ margin: 0 }}>No history recorded.</p>
                ) : history.map(h => {
                  const lines = (h.changes ?? []).filter(c => !HISTORY_HIDDEN_FIELDS.has(c.field));
                  if (h.action === 'create' && lines.length === 0) return null;
                  return (
                    <div key={h.id} style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--surface-tint)', border: '1px solid var(--border)', fontSize: 12.5 }}>
                      <div style={{ color: 'var(--text)' }}>
                        {h.action === 'create' ? 'Raised' : lines.map(describeChange).join(' · ')}
                      </div>
                      <div className="meta" style={{ marginTop: 3 }}>{h.changed_by_name || 'Staff'}{h.changed_by_role ? ` · ${h.changed_by_role}` : ''} · {fmtTime(h.changed_at)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
        <div className="modal-foot">
          <button className="btn btn-soft" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={handleSaveMeta} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </>
  );
}

// ── Create Modal ──────────────────────────────────────────────────────────────
function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [concernType, setConcernType] = useState<ConcernType>('academic');
  const [category,    setCategory]    = useState<ComplaintCategory>('teaching');
  const [priority,    setPriority]    = useState<Priority>('normal');
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [saving,      setSaving]      = useState(false);

  const catOptions = CATEGORIES.filter(([c]) =>
    concernType === 'academic' ? ACADEMIC_CATEGORIES.includes(c) : !ACADEMIC_CATEGORIES.includes(c));

  useEffect(() => {
    // keep category valid for the chosen type
    if (!catOptions.some(([c]) => c === category)) setCategory(catOptions[0][0]);
  }, [concernType]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!title.trim())       return toastMessage('Title is required', 'error');
    if (!description.trim()) return toastMessage('Description is required', 'error');
    setSaving(true);
    try {
      const res = await apiRequest(`${BASE}/complaints`, {
        method: 'POST',
        body: JSON.stringify({ concern_type: concernType, category, priority, title: title.trim(), description: description.trim() }),
      });
      if (!res.success) return toastMessage(res.message || 'Failed', 'error');
      toastMessage('Concern raised');
      onCreated();
    } catch { toastMessage('Failed', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="modal" style={{ width: 560, maxWidth: '95vw' }}>
        <div style={{ background: 'var(--slate)', color: '#fff', padding: '18px 24px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,0.12)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlertTriangle size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.3px' }}>New concern</div>
            <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>Raise a parent concern on their behalf</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><X size={17} /></button>
        </div>
        <div className="modal-body col gap-14" style={{ paddingTop: 18, paddingBottom: 22 }}>
          <div className="field">
            <label>Type</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['academic', 'non_academic'] as ConcernType[]).map(t => (
                <button key={t} type="button" onClick={() => setConcernType(t)}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${concernType === t ? 'var(--accent-deep)' : 'var(--border)'}`, background: concernType === t ? 'var(--accent-wash)' : 'transparent', color: 'var(--text)' }}>
                  {t === 'academic' ? 'Academic' : 'Non-academic'}
                </button>
              ))}
            </div>
          </div>
          <div className="row gap-14">
            <div className="field" style={{ flex: 1 }}>
              <label>Category</label>
              <select className="select" value={category} onChange={e => setCategory(e.target.value as ComplaintCategory)}>
                {catOptions.map(([c, label]) => <option key={c} value={c}>{label}</option>)}
              </select>
            </div>
            <div className="field" style={{ width: 150 }}>
              <label>Priority</label>
              <select className="select" value={priority} onChange={e => setPriority(e.target.value as Priority)}>
                {PRIORITIES.map(([val, c]) => <option key={val} value={val}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label>Title <span className="req">*</span></label>
            <input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Short summary" autoFocus />
          </div>
          <div className="field">
            <label>Description <span className="req">*</span></label>
            <textarea rows={4} value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the concern…"
              style={{ width: '100%', resize: 'vertical', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, lineHeight: 1.6, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-soft" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Raise concern'}</button>
        </div>
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ComplaintsPage() {
  const [items,       setItems]       = useState<Complaint[]>([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [typeTab,        setTypeTab]        = useState<'' | ConcernType>('');
  const [filterStatus,   setFilterStatus]   = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [classGroups,    setClassGroups]    = useState<CurriculumGroup[]>([]);
  const [staff,          setStaff]          = useState<StaffRef[]>([]);
  const [viewing, setViewing] = useState<Complaint | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    apiRequest<any>(`${BASE}/classes`).then(res => setClassGroups(res?.data?.grouped ?? [])).catch(() => {});
    // Staff pool for the assignee dropdown (degrades gracefully if not permitted).
    apiRequest<any>(`${BASE}/rbac/staff`).then(res => {
      const rows = res?.data?.data ?? [];
      setStaff(rows.map((s: any) => ({ id: s.id, name: s.name })));
    }).catch(() => {});
  }, []);

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(pg), limit: String(LIMIT) });
      if (search)         qs.set('search',       search);
      if (typeTab)        qs.set('concern_type', typeTab);
      if (filterStatus)   qs.set('status',       filterStatus);
      if (filterCategory) qs.set('category',     filterCategory);
      if (filterPriority) qs.set('priority',     filterPriority);
      selectedClasses.forEach(id => qs.append('class_id', id));
      const res = await apiRequest<{ data: Complaint[]; total: number }>(`${BASE}/complaints?${qs}`);
      // apiRequest resolves (doesn't throw) on HTTP error responses, so a
      // failed fetch must be checked explicitly or it silently renders as an
      // empty "No concerns found" state instead of an error.
      if (!res.success) { toastMessage(res.message || 'Failed to load', 'error'); setItems([]); setTotal(0); return; }
      setItems(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
      setPage(pg);
    } catch { toastMessage('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, [search, typeTab, filterStatus, filterCategory, filterPriority, selectedClasses]);

  useEffect(() => { load(1); }, [load]);

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }}>

      {/* Header */}
      <div className="page-head">
        <div className="page-head-icon"><AlertTriangle className="w-6 h-6" /></div>
        <div className="page-head-text">
          <h1>Help Desk</h1>
          <p>Track and resolve parent concerns</p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>New concern</button>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 32px', gap: 14, overflow: 'hidden', minHeight: 0 }}>

        {/* Type tabs */}
        <div className="tabs" style={{ flexShrink: 0 }}>
          {TYPE_TABS.map(t => (
            <button key={t.key} className={typeTab === t.key ? 'active' : ''} onClick={() => setTypeTab(t.key)}>{t.label}</button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
          <select className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            {STATUSES.map(([val, c]) => <option key={val} value={val}>{c.label}</option>)}
          </select>
          <select className="select" style={{ width: 'auto' }} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            <option value="">All Priority</option>
            {PRIORITIES.map(([val, c]) => <option key={val} value={val}>{c.label}</option>)}
          </select>
          <select className="select" style={{ width: 'auto' }} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="">All Categories</option>
            {CATEGORIES.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
          </select>
          <MultiClassSelect
            classGroups={classGroups}
            selectedIds={selectedClasses}
            onChange={ids => setSelectedClasses(ids)}
          />
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
            <input className="input" style={{ paddingLeft: 36, width: 200 }} placeholder="Search by title..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{total}</span>
            <span className="meta">concern{total !== 1 ? 's' : ''}</span>
          </div>
        </div>

        <ClassFilterChips classGroups={classGroups} selectedIds={selectedClasses} onChange={setSelectedClasses} />

        {/* Cards grid */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {loading ? (
            <div className="center" style={{ padding: '64px 20px' }}><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-deep)' }} /></div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty-art"><AlertTriangle size={32} /></div>
              <h3>No concerns found</h3>
              <p>No concerns match the current filters.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingBottom: 8 }}>
              {items.map(item => {
                const cfg = STATUS_CONFIG[item.status];
                const pri = PRIORITY_CONFIG[item.priority];
                return (
                  <div key={item.id} onClick={() => setViewing(item)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: `4px solid ${cfg.color}`, borderRadius: 10, overflow: 'hidden', cursor: 'pointer', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 0 }}>{item.title}</span>
                      {(item.priority === 'high' || item.priority === 'urgent') && (
                        <span style={{ fontSize: 10, fontWeight: 800, color: pri.color, textTransform: 'uppercase', flexShrink: 0 }}>{pri.label}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: cfg.color + '18', color: cfg.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} />
                        {cfg.label}
                      </span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: item.concern_type === 'academic' ? '#6d5bd018' : '#2a8c8c18', color: item.concern_type === 'academic' ? '#6d5bd0' : '#2a8c8c', fontWeight: 600 }}>
                        {item.concern_type === 'academic' ? 'Academic' : 'Non-academic'}
                      </span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--bg)', color: 'var(--text-2)', border: '1px solid var(--border)', fontWeight: 500 }}>{CATEGORY_LABELS[item.category]}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
                      {item.student && (
                        <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>
                          {item.student.display_name}{item.student.class ? ` · ${item.student.class.class_label}` : ''}
                        </span>
                      )}
                      {item.assignee && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>→ {item.assignee.name}</span>}
                      <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 'auto' }}>{fmt(item.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexShrink: 0 }}>
            <button className="btn btn-soft" disabled={page === 1} onClick={() => load(page - 1)}>Previous</button>
            <button className="btn btn-soft" disabled={page * LIMIT >= total} onClick={() => load(page + 1)}>Next</button>
          </div>
        )}

      </div>
    </div>

    {viewing && <DetailModal complaint={viewing} staff={staff} onClose={() => setViewing(null)} onUpdated={() => load(page)} />}
    {creating && <CreateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(1); }} />}
    </>
  );
}
